import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Search, ChevronLeft, ChevronRight, X } from "lucide-react";

const refHost = (r) => { if (!r) return ""; try { return new URL(r).hostname.replace(/^www\./, ""); } catch { return r.slice(0, 40); } };

export function RecentVisits({ days }) {
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { const t = setTimeout(() => { setTerm(q.trim()); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [days, size]);
  useEffect(() => {
    let live = true; setLoading(true);
    api.get(`/admin/traffic/visits?days=${days}&page=${page}&page_size=${size}&q=${encodeURIComponent(term)}`)
      .then((r) => { if (live) setD(r.data); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [days, page, size, term]);

  const total = d?.total || 0, pages = d?.pages || 1;
  const from = total ? (page - 1) * size + 1 : 0, to = Math.min(total, page * size);
  return (
    <div data-testid="recent-visits" className="bg-white rounded-2xl border border-sand-200 p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="font-heading font-bold">Recent visits <span className="text-muted-foreground font-medium text-sm">· {total.toLocaleString()} in the last {days} days</span></div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="visits-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Page, referrer, city or visitor…"
              className="rounded-xl border border-sand-200 pl-9 pr-8 py-2 text-sm w-64 outline-none focus:ring-2 focus:ring-redline-500/30" />
            {q && <button data-testid="visits-search-clear" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-ink"><X className="h-4 w-4" /></button>}
          </div>
          <select data-testid="visits-page-size" value={size} onChange={(e) => setSize(Number(e.target.value))} className="rounded-xl border border-sand-200 px-3 py-2 text-sm bg-white">
            {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
        </div>
      </div>
      <div className={`overflow-x-auto transition-opacity ${loading ? "opacity-50" : ""}`}>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b border-sand-200">
            <th className="py-2 pr-4 font-medium">Page</th><th className="py-2 pr-4 font-medium">Came from</th><th className="py-2 pr-4 font-medium">Location</th>
            <th className="py-2 pr-4 font-medium">Device</th><th className="py-2 pr-4 font-medium">Visitor</th><th className="py-2 font-medium">When</th>
          </tr></thead>
          <tbody>
            {(d?.items || []).map((r) => (
              <tr key={r.id} data-testid="visit-row" className="border-b border-sand-100">
                <td className="py-2 pr-4 truncate max-w-[220px]" title={r.path}>{r.path}</td>
                <td className="py-2 pr-4 text-muted-foreground truncate max-w-[160px]" title={r.referrer || ""}>{refHost(r.referrer) || "Direct"}</td>
                <td className="py-2 pr-4">{[r.city, r.region, r.country].filter(Boolean).join(", ") || "Unknown"}</td>
                <td className="py-2 pr-4 text-muted-foreground whitespace-nowrap">{r.device}</td>
                <td className="py-2 pr-4">
                  <button data-testid="visit-visitor" onClick={() => setQ(r.visitor_id)} title="Show every visit from this visitor"
                    className="font-mono text-xs rounded-md bg-sand-100 hover:bg-sand-200 px-1.5 py-0.5 transition-colors">{(r.visitor_id || "").slice(-6)}</button>
                </td>
                <td className="py-2 text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
              </tr>
            ))}
            {d && d.items.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No visits match.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
        <span data-testid="visits-range">{total ? `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}` : "—"}</span>
        <div className="flex items-center gap-1">
          <button data-testid="visits-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-sand-200 p-1.5 hover:border-ink disabled:opacity-40 disabled:hover:border-sand-200"><ChevronLeft className="h-4 w-4" /></button>
          <span data-testid="visits-page" className="px-2 font-medium text-ink">Page {page} of {pages}</span>
          <button data-testid="visits-next" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-sand-200 p-1.5 hover:border-ink disabled:opacity-40 disabled:hover:border-sand-200"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}
